import moment, {type Moment} from 'moment-timezone'
import {mapMaybes, maybe} from '@freckle/maybe'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  field,
  number,
  string,
  oneOf,
  array,
  boolean,
  nullable,
  date,
  record,
  mapStatic,
} from '@freckle/parser'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {parser as identityProviderParser} from '@freckle/educator-entities/ts/roster/identity-provider'

type StarStatus = 'star_enabled' | 'star_disabled' | 'star_unset'

const allStarStatuses: StarStatus[] = ['star_enabled', 'star_disabled', 'star_unset']

type RenaissanceSchoolClientT = {
  platform: string
  clientId: string
  rpid: string
  baseUrl: string
  starStatus: StarStatus
  sharedRosteringEnabled: boolean
}
export type SchoolAttrs = {
  id: number
  name: string
  createdAt: Moment
  updatedAt: Moment
  districtId: number
  sisId: string | undefined | null
  ncesSchoolId: string | undefined | null
  cleverSchoolId: string | undefined | null
  classLinkSchoolId: string | undefined | null
  renaissanceRpIdentifier: string | undefined | null
  city: string
  administrativeArea: string | undefined | null
  postalCode: string | undefined | null
  formattedAddressLines: Array<string> | undefined | null
  countryCode: string
  licenseUtilizationNotificationsMutedAt: Moment | undefined | null
  courseSubjectsByLicensing: boolean
  renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null
  activeIdp: IdentityProviderT | undefined | null
  syncIdp: boolean
  crmId: string | undefined | null
  active: boolean // a school can be active/inactive in RL CRM. As that becomes our source-of-truth, we mirror that data.
  crmAccountNumber: number | undefined | null
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
}

const parseRenaissanceSchoolClient: ParserT<RenaissanceSchoolClientT> = record({
  platform: string(),
  clientId: string(),
  rpid: string(),
  baseUrl: string(),
  starStatus: oneOf<StarStatus>('StarStatus', allStarStatuses),
  sharedRosteringEnabled: boolean(),
})

const parseSchoolAttrs: ParserT<SchoolAttrs> = record({
  id: number(),
  name: string(),
  createdAt: field(mapStatic(date(), moment), 'created-at'),
  updatedAt: field(mapStatic(date(), moment), 'updated-at'),
  districtId: field(number(), 'district-id'),
  sisId: field(nullable(string()), 'sis-id'),
  ncesSchoolId: field(nullable(string()), 'nces-school-id'),
  cleverSchoolId: field(nullable(string()), 'clever-school-id'),
  classLinkSchoolId: field(nullable(string()), 'class-link-school-id'),
  renaissanceRpIdentifier: field(nullable(string()), 'renaissance-r-p-identifier'),
  city: string(),
  administrativeArea: field(nullable(string()), 'administrative-area'),
  postalCode: field(nullable(string()), 'postal-code'),
  formattedAddressLines: field(nullable(array(string())), 'formatted-address-lines'),
  countryCode: field(string(), 'country-code'),
  licenseUtilizationNotificationsMutedAt: field(
    nullable(mapStatic(date(), moment)),
    'license-utilization-notifications-muted-at',
  ),
  courseSubjectsByLicensing: field(boolean(), 'course-subjects-by-licensing'),
  renaissanceSchoolClient: field(
    nullable(parseRenaissanceSchoolClient),
    'renaissance-school-client',
  ),
  activeIdp: field(nullable(identityProviderParser), 'active-idp'),
  syncIdp: field(boolean(), 'sync-idp'),
  crmId: field(nullable(string()), 'crm-id'),
  active: field(boolean(), 'active'),
  crmAccountNumber: field(nullable(number()), 'crm-account-number'),
  identityProvider: field(nullable(identityProviderParser), 'identity-provider'),
  idpManaged: field(boolean(), 'idp-managed'),
})

export const parseSchool = Parser.mkRun<SchoolAttrs>(parseSchoolAttrs)

const parseSchools = Parser.mkRun<Array<SchoolAttrs>>(array(parseSchoolAttrs))
export function schoolHasSharedRostering(schoolAttrs: SchoolAttrs): boolean {
  return maybe(
    () => false,
    client => client.sharedRosteringEnabled,
    schoolAttrs.renaissanceSchoolClient,
  )
}

// Subtype to support DetailedSchoolT, BasicSchoolT, etc used in Console:
type SchoolRenaissanceSchoolClientFieldsT = Inexact<{
  renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null
}>

export const schoolHasStarIntegration = (
  schoolAttrs: SchoolRenaissanceSchoolClientFieldsT,
): boolean => schoolAttrs.renaissanceSchoolClient?.starStatus === 'star_enabled'

export function getActiveIdps(schools: Array<SchoolAttrs>): Set<IdentityProviderT> {
  return new Set(mapMaybes(schools, school => school.activeIdp))
}

export async function fetchSchools(): Promise<Array<SchoolAttrs>> {
  return fancyClient.GET('/2/schools').then(extractData).then(parseSchools)
}

export async function fetchSchool(id: number): Promise<SchoolAttrs> {
  return fancyClient
    .GET('/2/schools/{SchoolP-SchoolId}', {
      params: {
        path: {
          'SchoolP-SchoolId': id,
        },
      },
    })
    .then(extractData)
    .then(parseSchool)
}

export async function fetchSchoolsInDistrict(districtId: number): Promise<Array<SchoolAttrs>> {
  return fancyClient
    .GET('/2/districts/{DistrictP-DistrictId}/schools', {
      params: {
        path: {
          'DistrictP-DistrictId': districtId,
        },
      },
    })
    .then(extractData)
    .then(parseSchools)
}
